import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from "lottie-react";
import octopusAnimation from "../assets/Octopus.json";

const rankIcons = {
  School: '🏫',
  State: '🌆',
  Country: '🇮🇳',
  World: '🌍',
};

interface RankData {
  userId: string;
  name: string;
  points: number;
  profilePic?: string;
}

interface MyRankData {
  rank: number;
  points: number;
  profilePic?: string;
}

interface RankListProps {
  userId: string;
  userName: string;
  onBack: () => void;
}

const RankItem: React.FC<{
  person: RankData;
  index: number;
  userId: string;
  getRankStyle: (index: number, isUser: boolean) => string;
}> = ({ person, index, userId, getRankStyle }) => {
  console.log(`Rendering RankItem for user: ${person.name}, index: ${index}, isUser: ${person.userId === userId}`); // Debug log
  return (
    <motion.div
      className={`flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all duration-300 touch-manipulation ${getRankStyle(index, person.userId === userId)}`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      role="listitem"
      aria-label={`Rank ${index + 1}: ${person.name}, ${person.points} points`}
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 flex items-center justify-center text-sm font-bold text-gray-200 bg-gray-900/50 rounded-full backdrop-blur-sm">
          #{index + 1}
        </span>
        <div className="relative">
          <img
            src={person.profilePic || 'https://randomuser.me/api/portraits/lego/1.jpg'}
            alt={`${person.name}'s profile`}
            className="w-10 h-10 rounded-full border-2 border-neon-green/50 shadow-neon-green/30 object-cover transition-transform duration-300 hover:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-neon-green/20 to-neon-cyan/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
        </div>
        <p className={`text-sm font-medium ${person.userId === userId ? 'text-neon-green' : 'text-white'}`}>
          {person.name} {person.userId === userId && <span className="text-xs text-neon-cyan">(You)</span>}
        </p>
      </div>
      <div className="text-right">
        <p className={`text-sm font-bold ${person.userId === userId ? 'text-neon-green' : 'text-white'}`}>
          {person.points}
        </p>
        <p className="text-xs text-gray-400">Points</p>
      </div>
    </motion.div>
  );
};

const MyRankItem: React.FC<{ rankData: MyRankData }> = ({ rankData }) => {
  console.log('Rendering MyRankItem with rankData:', rankData); // Debug log
  return (
    <motion.div
      className="my-rank-card flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all duration-300 touch-manipulation sticky top-0 z-10 backdrop-blur-lg"
      style={{
        backgroundColor: 'rgba(168, 253, 45, 0.7)', // Solid neon green (#a8fd2d) with 70% opacity
        border: '1px solid rgba(168, 253, 45, 0.6)', // Neon green border
        boxShadow: '0 0 10px rgba(168, 253, 45, 0.5)', // Neon green shadow
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      role="listitem"
      aria-label={`Your rank: ${rankData.rank}, ${rankData.points} points`}
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 flex items-center justify-center text-sm font-bold text-gray-200 bg-gray-900/50 rounded-full backdrop-blur-sm">
          #{rankData.rank}
        </span>
        <div className="relative">
          <img
            src={rankData.profilePic || 'https://randomuser.me/api/portraits/lego/1.jpg'}
            alt="Your profile"
            className="w-10 h-10 rounded-full border-2 border-neon-green/50 shadow-neon-green/30 object-cover transition-transform duration-300 hover:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-neon-green/20 to-neon-cyan/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
        </div>
        <p className="text-sm font-medium text-white">My Rank</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-bold text-neon-green">{rankData.points}</p>
        <p className="text-xs text-gray-400">Points</p>
      </div>
    </motion.div>
  );
};

const RankList: React.FC<RankListProps> = ({ userId, userName, onBack }) => {
  const [viewMode, setViewMode] = useState<string>('country');
  const [rankData, setRankData] = useState<RankData[]>([]);
  const [myRankData, setMyRankData] = useState<MyRankData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [availableModes, setAvailableModes] = useState<string[]>(['state', 'country', 'world']);

  const getRankStyle = (index: number, isUser: boolean): string => {
    if (isUser) return 'bg-gradient-to-r from-neon-green/30 to-neon-cyan/30 border border-neon-green/60 shadow-neon-green/40 backdrop-blur-sm';
    switch (index) {
      case 0:
        return 'bg-gradient-to-r from-yellow-500/30 to-yellow-600/30 border border-yellow-500/40 shadow-yellow-500/30 backdrop-blur-sm';
      case 1:
        return 'bg-gradient-to-r from-gray-400/30 to-gray-500/30 border border-gray-400/40 shadow-gray-400/30 backdrop-blur-sm';
      case 2:
        return 'bg-gradient-to-r from-amber-600/30 to-amber-700/30 border border-amber-600/40 shadow-amber-600/30 backdrop-blur-sm';
      default:
        return 'bg-gray-800/30 hover:bg-gray-700/40 border border-gray-700/30 backdrop-blur-sm';
    }
  };

  const fetchRankings = async (mode: string) => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      console.log('Fetching rankings for userId:', userId, 'mode:', mode); // Debug log
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/rankings/${mode}/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401 && errorData.message === 'Token expired, please log in again') {
          localStorage.removeItem('token');
          return;
        }
        throw new Error(errorData.message || 'Failed to fetch rankings');
      }

      const data = await response.json();
      console.log('API response:', data); // Debug log
      setRankData(data.rankings);
      setMyRankData(data.myRank);
    } catch (err: any) {
      setError(err.message || 'Error fetching rankings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkSchoolConnection = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/user`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 401 && errorData.message === 'Token expired, please log in again') {
            localStorage.removeItem('token');
            return;
          }
          throw new Error(errorData.message || 'Failed to fetch user data');
        }

        const userData = await response.json();
        if (userData.school || userData.schoolDetails) {
          setAvailableModes(['school', 'state', 'country', 'world']);
          setViewMode('school');
        } else {
          setAvailableModes(['state', 'country', 'world']);
          setViewMode('state');
        }
      } catch (err: any) {
        console.error('Error checking school connection:', err);
        setError('Failed to verify school connection');
      }
    };

    if (userId) {
      checkSchoolConnection();
    }
  }, [userId]);

  useEffect(() => {
    if (userId && viewMode) {
      fetchRankings(viewMode);
    }
  }, [viewMode, userId]);

  console.log('Current viewMode:', viewMode); // Debug log

  return (
    <motion.div
      className="min-h-screen bg-dark-blue flex flex-col items-center p-4 sm:p-6"
      style={{
        marginTop: window.innerWidth <= 768 ? '100px' : '120px',
        backgroundColor: '#1c2526', // Inline style to force dark blue
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="w-full max-w-md sm:max-w-lg">
        <motion.div
          className="p-4 sm:p-6 bg-gradient-to-br from-gray-900/50 to-gray-800/50 rounded-3xl shadow-2xl backdrop-blur-lg border border-gray-700/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              DroctoIQ Leaderboard
              <motion.span
                className="text-neon-green"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                🏆
              </motion.span>
            </h3>
          </div>

          <div className="mb-4 sm:mb-6 flex flex-wrap gap-2">
            {availableModes.map((mode) => (
              <motion.button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm sm:text-base transition-all duration-200 ${
                  viewMode === mode
                    ? 'bg-neon-green !text-black shadow-neon-green/60'
                    : 'bg-gray-800/50 text-white hover:bg-gray-700/60'
                } focus:outline-none focus:ring-2 focus:ring-neon-green/50 touch-manipulation backdrop-blur-sm`}
                style={
                  viewMode === mode
                    ? { backgroundColor: '#a8fd2d' } // Inline style to force neon green
                    : {}
                }
                whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(168, 253, 45, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                aria-label={`View ${mode} leaderboard`}
              >
                <motion.span
                  animate={{ scale: viewMode === mode ? [1, 1.2, 1] : 1 }}
                  transition={{ repeat: viewMode === mode ? Infinity : 0, duration: 1.5 }}
                >
                  {rankIcons[mode.charAt(0).toUpperCase() + mode.slice(1)]}
                </motion.span>
                <span>{mode.charAt(0).toUpperCase() + mode.slice(1)}</span>
              </motion.button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center items-center">
              <div className="spinner"></div>
            </div>
          ) : error ? (
            <p className="text-cosmic-pink text-center">{error}</p>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-neon-green/60 scrollbar-track-gray-900/50 pr-2">
              <AnimatePresence>
                {myRankData && <MyRankItem rankData={myRankData} />}
                {rankData.map((person, index) => (
                  <RankItem
                    key={`${viewMode}-${person.userId}`}
                    person={person}
                    index={index}
                    userId={userId}
                    getRankStyle={getRankStyle}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.div>
      </div>

      <style>{`
        :root {
          --neon-green: #a8fd2d;
          --neon-cyan: #22c1c3;
          --neon-blue: #1e90ff;
          --neon-purple: #9b59b6;
          --dark-blue: #1c2526; /* Dark blue background */
          --cosmic-pink: #ff1493;
        }

        .bg-neon-green {
          background-color: var(--neon-green) !important;
        }

        .bg-dark-blue {
          background-color: var(--dark-blue) !important;
        }

        .my-rank-card {
          background-color: rgb(17, 0, 255) !important;
          border: 1px solid rgb(255, 255, 255) !important;
        }

        .scrollbar-thin {
          scrollbar-width: thin;
        }

        .scrollbar-thumb-neon-green\\/60 {
          scrollbar-color: rgba(168, 253, 45, 0.6) rgba(10, 10, 10, 0.5);
        }

        .scrollbar-track-gray-900\\/50 {
          scrollbar-color: rgba(10, 10, 10, 0.5);
        }

        .touch-manipulation {
          touch-action: manipulation;
        }

        .backdrop-blur-sm {
          backdrop-filter: blur(8px);
        }

        .backdrop-blur-lg {
          backdrop-filter: blur(12px);
        }

        @media (hover: hover) {
          .hover\\:scale-110:hover {
            transform: scale(1.1);
          }
        }



        .shadow-neon-green\\/40 {
          animation: glow 2s ease-in-out infinite;
        }

        /* Spinner Animation */
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(168, 253, 45, 0.2);
          border-top: 4px solid var(--neon-green);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </motion.div>
  );
};

export default RankList;